import { useRef, useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Button } from "antd";
import Vimeo from "@u-wave/react-vimeo";
import { WysiwygDisplay } from "./WysiwygDisplay";
import { saveProgressAsync, selectCourse } from "../features/courses/coursesSlice";
import useInactivity from "../hooks/useInactivity";
import { ReactComponent as Play } from "../assets/icons/Play.svg";
import { ReactComponent as Pause } from "../assets/icons/Pause.svg";
import { ReactComponent as Rewind } from "../assets/icons/Rewind.svg";
import { ReactComponent as Video } from "../assets/icons/Video.svg";
import StorageService from "../util/storage";

// Constants and Utility Functions
const vimeoURLRegex =
    /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[^/]+\/videos\/|album\/\d+\/video\/|)(\d+)/;
const vimeoRegex =
    /(?:<iframe[^>]*src=")?(https?:\/\/(?:www\.)?vimeo\.com\/(?:video\/|channels\/[\w]+\/|groups\/[^/]+\/videos\/|album\/\d+\/video\/|)(\d+))[^>]*(?:<\/iframe>)?/;

const extractVimeoUrlAndId = (urlOrEmbedCode) => {
    const match = urlOrEmbedCode.match(vimeoRegex);
    return match ? { fullUrl: match[1], vimeoId: match[2] } : null;
};

const isURL = (url) => /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/.test(url);

// Component Definition
export const CourseEmbed = ({ lesson, controls, setControls }) => {
    // State and Refs
    const dispatch = useDispatch();
    const { id } = useParams();
    const data = useSelector((state) => selectCourse(state));
    const [player, setPlayer] = useState(undefined);
    const isMount = useRef(false);
    const iframeRef = useRef(null);
    const [videoWatched, setVideoWatched] = useState(false);
    const { isInactive, isTabInactive } = useInactivity();
    const isPlaying = useRef(false);
    const [playing, setPlaying] = useState(false);
    const [err, setErr] = useState(null);

    // Callbacks and Handlers
    const saveProgress = useCallback(
        async (paused, ended) => {
            if (!player) return;
            dispatch(
                saveProgressAsync({
                    id,
                    data: {
                        progress: {
                            ...(data.attributes.progress ?? {}),
                            [`${lesson.__typename}${lesson.id}`]: {
                                currentTime: await player.getCurrentTime(),
                                duration: await player.getDuration(),
                                paused,
                                ended,
                            },
                        },
                    },
                })
            );
        },
        [data.attributes.progress, dispatch, id, lesson, player]
    );

    const onVideoEnd = useCallback(async () => {
        setVideoWatched(true);
        await saveProgress(false, true);
    }, [saveProgress]);

    const onVideoPause = useCallback(
        async (paused) => {
            await saveProgress(paused, false);
        },
        [saveProgress]
    );

    const onPlayerPause = useCallback(() => {
        isPlaying.current = false;
    }, []);
    const onPlayerPlay = useCallback(() => {
        isPlaying.current = true;
    }, []);
    const onPlayerEnd = useCallback(async () => {
        await onVideoEnd();
    }, [onVideoEnd]);
    const onPlayerReady = useCallback(
        (e) => {
            if (!player) {
                setPlayer(e);
            }
        },
        [player]
    );
    const onPlayerError = useCallback((e) => console.log(">>> ERROR", e), []);

    // Render Logic
    const handleOnPlay = useCallback(() => {
        setPlaying(true);
        if (player && !isPlaying.current) {
            player.play().catch((error) => {
                console.log("Error playing video:", error);
            });
        }
    }, [player]);

    const handleOnPause = useCallback(() => {
        setPlaying(false);
        if (player && isPlaying.current) {
            player.pause().catch((error) => {
                console.log("Error pausing video:", error);
            });
        }
    }, [player]);

    const handleOnRewind = useCallback(async () => {
        if (player) {
            const currentTime = await player.getCurrentTime();
            const newTime = currentTime <= 15 ? 0 : currentTime - 15;
            player.setCurrentTime(newTime);
        }
    }, [player]);

    const skipToEnd = useCallback(async () => {
        if (player) {
            const duration = await player.getDuration();
            player.setCurrentTime(duration - 10);
        }
    }, [player]);

    const mute = useCallback(() => {
        if (player) {
            player.setVolume(0);
        }
    }, [player]);

    // Effects
    useEffect(() => {
        if (player && !isMount.current) {
            const playHandler = (e) => {
                setControls({ ...controls, state: "playing", attributes: e });
                onPlayerPlay(e);
            };
            const pauseHandler = (e) => {
                setControls({ ...controls, state: "pause", attributes: e });
                onPlayerPause(e);
            };
            const endHandler = (e) => {
                setControls({ ...controls, state: "ended", attributes: e });
                onPlayerEnd(e);
            };
            const errorHandler = (e) => {
                setControls({ ...controls, state: "error", attributes: e });
                onPlayerError(e);
            };

            player.on("play", playHandler);
            player.on("pause", pauseHandler);
            player.on("ended", endHandler);
            player.on("error", errorHandler);

            isMount.current = true;

            return () => {
                player.off("play", playHandler);
                player.off("pause", pauseHandler);
                player.off("ended", endHandler);
                player.off("error", errorHandler);
                isMount.current = false;
            };
        }
    }, [player, controls, onPlayerEnd, onPlayerError, onPlayerPause, onPlayerPlay, setControls]);

    useEffect(() => {
        const { embedURL } = lesson || {};
        if (!embedURL) return;

        const isVimeo = vimeoURLRegex.test(embedURL);
        setControls(isVimeo ? { video: true, state: "loaded" } : false);
    }, [lesson, setControls]);

    useEffect(() => {
        if (data.attributes.progress) {
            const lessonProgress = data.attributes.progress[`${lesson.__typename}${lesson.id}`];
            if (lessonProgress) {
                const { currentTime, ended } = lessonProgress;
                if (player && typeof currentTime === "number" && !isNaN(currentTime) && !ended) {
                    player.setCurrentTime(currentTime);
                    // player.play();
                }
                if (ended) {
                    setVideoWatched(true);
                }
            }
        }
    }, [data.attributes.progress, lesson.__typename, lesson.id, player]);

    useEffect(() => {
        if(videoWatched) {
            setControls(null)
        }
    },  [videoWatched, setControls])

    useEffect(() => {
        // video already watched or not
        const videoWatched = data.attributes.progress?.[`${lesson.__typename}${lesson.id}`]?.ended;
        if (isTabInactive && !videoWatched) {
            handleOnPause();
            if ("Notification" in window && Notification.permission === "granted") {
                new Notification("Your course is still in progress", {
                    body: "Please return to complete watching your video.",
                    // icon: "/path/to/icon.png", // Optional: Add an icon for the notification
                });
            }

            // save video progress to continue later
            onVideoPause().then(() => {
                console.log("Video paused and saved");
            });
        }
    }, [data.attributes.progress, handleOnPause, isInactive, isTabInactive, lesson.__typename, lesson.id, onVideoPause]);

    const renderVimeoPlayer = useMemo(() => {
        setErr(null);
        if (!lesson?.embedURL) return null;

        const urlAndId = extractVimeoUrlAndId(lesson.embedURL);
        if (!urlAndId) return null;

        const { fullUrl } = urlAndId;
        const user = StorageService.getItem("user")
        const isUATuser = ['TaxiUAT1', 'TaxiUAT2'].includes(user?.user_identifier)
        const _isDev = process.env.NODE_ENV === "development" || isUATuser;

        return (
            <>
                {!videoWatched ? (
                    <div className="TextSpeech">
                        <div>
                            <Video /> 
                        </div>
                        {playing ? (
                            <Button onClick={handleOnPause}>
                                <Pause /> Pause
                            </Button>
                        ) : (
                            <Button onClick={handleOnPlay}>
                                <Play /> Play
                            </Button>
                        )}
                        <Button onClick={handleOnRewind}>
                            <Rewind style={{ width: "22px", height: "22px" }} /> Rewind (15s)
                        </Button>
                        {_isDev && <Button onClick={skipToEnd}>Skip (Admin)</Button>}
                        {/* {_isDev && <Button onClick={mute}>Mute</Button>} */}
                    </div>
                ) : null}
                <div className="VideoEmbed">
                    <Vimeo
                        key={`${fullUrl}-${videoWatched}`} // Change key based on videoWatched state
                        video={fullUrl}
                        width={640}
                        height={480}
                        onReady={onPlayerReady}
                        controls={videoWatched}
                        showPortrait={false}
                        showTitle={false}
                        onError={async err=> {
                            setErr("Video URL is invalid. Please contact an admin.")
                            await onVideoEnd()
                        }}
                    />
                </div>
            </>
        );
    }, [handleOnPause, handleOnPlay, handleOnRewind, lesson.embedURL, mute, onPlayerReady, playing, skipToEnd, videoWatched]);

    const renderIframe = useMemo(() => {
        if (!lesson?.embedURL) return null;

        const isVimeo = vimeoURLRegex.test(lesson.embedURL);
        const isValidURL = isURL(lesson.embedURL);
        if (isVimeo) return null; // Vimeo handled separately

        if (isValidURL) {
            return (
                <iframe
                    ref={iframeRef}
                    title={lesson?.title}
                    src={lesson.embedURL}
                    width="100%"
                    height="800px"
                    style={{ border: 0 }}
                    allow="autoplay fullscreen"
                />
            );
        } else {
            return <WysiwygDisplay data={lesson?.embedURL} className="VideoEmbed" />;
        }
    }, [lesson, isURL]);

    // Return Statement
    if (!lesson) return null;

    return (
        <div>
            <h2>{lesson?.title}</h2>
            <WysiwygDisplay data={lesson?.description} />
            {err && <p style={{color: 'red'}}>{err}</p>}
            {vimeoURLRegex.test(lesson.embedURL) ? renderVimeoPlayer : renderIframe}
        </div>
    );
};
